import { useParams, useNavigate } from 'react-router-dom';
import { auditCaseStudies } from '../data/auditCaseStudies'; // Re-verify import
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, ShieldCheck, TrendingUp, AlertTriangle, Cog, ExternalLink, ChevronRight } from 'lucide-react';
import { useTranslations } from '../i18n';
import { SEO } from '../components/ui/SEO';

export function AuditCaseStudyView() {
  const { studySlug } = useParams<{ studySlug: string }>();
  const navigate = useNavigate();
  const { language, t } = useTranslations();

  const study = auditCaseStudies.find((s) => s.slug === studySlug);

  if (!study) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-slate-50 dark:bg-slate-950">
        <h1 className="text-4xl font-black text-slate-900 dark:text-white mb-4">404</h1>
        <p className="text-xl text-slate-600 dark:text-slate-400 mb-8 font-medium">
          {language === 'lv' ? 'Pētījums nav atrasts' : 'Case study not found'}
        </p>
        <button
          onClick={() => navigate('/audit')}
          className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-black hover:bg-blue-500 transition-all"
        >
          Back to Audit
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent font-primary text-slate-900 dark:text-slate-100 flex flex-col">
      <SEO 
        title={`${study.verdict}: ${study.title} | ${study.carParams?.make} ${study.carParams?.model} Audit`}
        description={study.summary}
      />
      
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate('/audit')}
            className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-blue-600 transition-colors font-bold"
          >
            <ArrowLeft size={20} />
            {t.audit.caseStudy.backToAudit}
          </button>
          
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} className="text-blue-600" />
            <span className="font-black text-sm uppercase tracking-widest hidden sm:block">{t.audit.caseStudy.oracleCaseStudy}</span>
          </div>
        </div>
      </header>

      <main className="flex-1 py-12 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {/* Badge & Title */}
          <div className="text-center mb-12">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest mb-6 ${
              study.verdict === 'LETHAL' ? 'bg-red-100 text-red-600' :
              study.verdict === 'REPAIRABLE' ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'
            }`}>
              {study.verdict} {language === 'lv' ? 'VERDIKTS' : 'VERDICT'}
            </div>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight mb-6">
              {study.title}
            </h1>
            <div className="flex items-center justify-center gap-8 text-sm font-bold text-slate-500">
              <div className="flex items-center gap-2">
                {study.icon}
                {study.category}
              </div>
              <div className="flex items-center gap-2 text-emerald-600">
                <TrendingUp size={18} />
                {study.savings} {language === 'lv' ? 'Ietaupījums' : 'Savings'}
              </div>
            </div>

            {study.carParams && (
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                {Object.entries(study.carParams).map(([key, value]) => (
                  <div key={key} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-4 py-2 rounded-2xl flex flex-col items-center min-w-[100px]">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{key}</span>
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* The Question Card */}
          <div className="relative mb-12 group">
            <div className="absolute -inset-1 bg-gradient-to-r from-slate-200 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-[2.5rem] blur opacity-25"></div>
            <div className="relative bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 sm:p-12 border border-slate-200 dark:border-slate-800 shadow-xl">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-6 flex items-center gap-2">
                <span className="w-8 h-px bg-slate-200 dark:bg-slate-800"></span>
                {t.audit.caseStudy.realUserQuestion}
                <span className="w-8 h-px bg-slate-200 dark:bg-slate-800"></span>
              </div>
              <p className="text-xl sm:text-2xl font-bold italic text-slate-700 dark:text-slate-300 leading-relaxed text-center">
                "{study.question}"
              </p>
            </div>
          </div>

          {/* The Oracle Response */}
          <div className="bg-slate-900 text-slate-100 rounded-[3rem] p-8 sm:p-16 shadow-2xl relative overflow-hidden border border-slate-800">
            {/* Background elements */}
            <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
                <ShieldCheck size={300} strokeWidth={1} />
            </div>

            <div className="relative z-10">
                <div className="flex items-center gap-3 mb-10">
                    <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                        <ShieldCheck size={28} className="text-white" />
                    </div>
                    <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-blue-400 mb-0.5">{t.audit.caseStudy.officialAudit}</div>
                        <div className="text-lg font-black text-white">{t.audit.caseStudy.fullReport}</div>
                    </div>
                </div>

                <div className="prose prose-invert max-w-none 
                    prose-headings:font-black prose-headings:tracking-tight 
                    prose-p:text-slate-300 prose-p:leading-relaxed prose-p:text-lg
                    prose-strong:text-white prose-strong:font-black
                    prose-code:text-blue-400 prose-code:bg-blue-900/20 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md
                    prose-li:text-slate-300">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {study.fullResponse}
                    </ReactMarkdown>
                </div>

                <div className="mt-16 pt-12 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-8">
                    <div className="flex flex-col items-center sm:items-start gap-2">
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{t.audit.caseStudy.economicImpact}</div>
                        <div className="text-3xl font-black text-emerald-500">+{study.savings}</div>
                    </div>
                    
                    <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black text-lg transition-all shadow-xl shadow-blue-500/20 hover:scale-105 active:scale-95 flex items-center gap-2"
                    >
                        {t.audit.caseStudy.startYourAudit}
                        <ExternalLink size={20} />
                    </button>
                </div>
            </div>
          </div>
          
          <div className="mt-20">
            <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-8 flex items-center gap-4">
              {t.audit.caseStudy.moreAudits}
              <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800 ml-4"></div>
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-6">
              {auditCaseStudies.filter(s => s.slug !== studySlug).sort(() => 0.5 - Math.random()).slice(0, 4).map(s => (
                <div 
                  key={s.slug}
                  onClick={() => {
                    navigate(`/audit/case-study/${s.slug}`);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 flex items-center justify-between group cursor-pointer hover:border-blue-500 transition-all shadow-sm"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-slate-50 dark:bg-slate-950 rounded-xl group-hover:bg-blue-50 dark:group-hover:bg-blue-900/30 transition-colors">
                        {s.icon}
                    </div>
                    <h3 className="font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors text-sm">
                      {s.title}
                    </h3>
                  </div>
                  <ChevronRight size={20} className="text-slate-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-20 text-center">
            <p className="text-slate-500 font-medium mb-4">{t.audit.caseStudy.sharingIsCaring}</p>
            <div className="flex items-center justify-center gap-4">
                {/* Simplified share buttons */}
                {[(language === 'lv' ? 'Dalīties' : 'Share'), 'Twitter', 'Facebook', (language === 'lv' ? 'Kopēt saiti' : 'Copy Link')].map((label) => (
                    <button key={label} className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-900 hover:bg-blue-50 dark:hover:bg-blue-900/30 text-slate-600 dark:text-slate-400 hover:text-blue-600 transition-all font-bold text-sm">
                        {label}
                    </button>
                ))}
            </div>
          </div>
        </div>
      </main>

      <footer className="py-12 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 mt-20 text-center">
          <p className="text-slate-400 text-xs font-black uppercase tracking-widest">{t.audit.caseStudy.poweredBy}</p>
      </footer>
    </div>
  );
}
